/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from "react";
// UI Frameworks
import { Table, Typography, Icon } from "antd";
import TitleBox from "components/TitleBox";
// Hooks and utilities
import { isEmpty } from "lodash";
// Hooks and Utilities
import jMoment from "moment-jalaali";
import addCommas from "helpers/index";
import FinancialService, {
	FinancialType,
	AccountingStatus,
	AccountingSubject,
} from "services/http/endpoints/financial";
// styles
import styles from "./index.module.scss";

const { Text } = Typography;

const Financials: React.FC = () => {
	const [loading, setLoading] = React.useState(true);
	const [pagination, setPagination] = React.useState();
	const [financials, setFinancial] = React.useState([] as FinancialType[]);

	const fetchData = async (params?: any) => {
		try {
			setLoading(true);
			FinancialService.clearOrder();
			FinancialService.clearFilter();
			FinancialService.size = params.size;
			FinancialService.page = params.page;
			FinancialService.makeOrderBy("createdDate", false);
			FinancialService.makeFilters("accountHeadline", "EQ", "USER");
			if (typeof params.filters === "object") {
				// eslint-disable-next-line array-callback-return
				Object.keys(params.filters).map(key => {
					if (params.filters[key].length > 0) {
						FinancialService.makeFilters(key, "IN", params.filters[key].join());
					}
				});
			}
			const result = await FinancialService.search(FinancialService.getSearchFilter());
			const pager = { ...pagination };
			// Read total count from server
			pager.total = result.pagination.total;

			setLoading(false);
			setPagination(pager);
			setFinancial(result.result);
		} catch (error) {
			setLoading(false);
		}
	};

	// get facilities
	React.useEffect(() => {
		fetchData({ size: 10, page: 1 });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function handleTableChange(_pagination: any, filters: any, sorter: any) {
		const pager = { ...pagination };
		pager.current = _pagination.current;
		setPagination({
			pagination: pager,
		});
		fetchData({
			size: _pagination.pageSize,
			page: _pagination.current,
			sortField: sorter.field,
			sortOrder: sorter.order,
			filters,
		});
	}

	function accountingStatusFormatted(status: string) {
		switch (status) {
			case AccountingStatus.DEBTOR:
				return (
					<Text type="danger">
						<Icon type="arrow-down" /> بدهکار
					</Text>
				);
			case AccountingStatus.CREDITOR:
				return (
					<Text style={{ color: "#0b8235" }}>
						<Icon type="arrow-up" /> بستانکار
					</Text>
				);
			default:
				return "-";
		}
	}

	function accountingSubjectFormatted(status: string) {
		switch (status) {
			case AccountingSubject.SETTLEMENT:
				return "تسویه حساب ریالی";
			case AccountingSubject.CHARGE_RIAL_CREDIT:
				return "شارژ کیف پول";
			case AccountingSubject.SELL_BAAZAR:
				return "فروش طلا";
			case AccountingSubject.BUY_BAAZAR:
				return "خرید طلا";
			case AccountingSubject.BLOCK_OR_UNBLOCK_GOLD:
				return "مسدود یا آزاد سازی طلا";
			case AccountingSubject.BUY_BAAZAR_WAGE:
				return "کارمزد خرید طلا";
			case AccountingSubject.BUY_CREDIT_BAAZAR_DEBT:
				return "بدهی خرید اعتباری طلا";
			case AccountingSubject.BUY_CREDIT_BAAZAR_DEPOSIT:
				return "بیعانه خرید اعتباری طلا";
			case AccountingSubject.CLEARING_CREDIT_BAAZAR:
				return "تسویه";
			case AccountingSubject.MANUALLY_MANAGE_GOD_WALLET:
				return "صندوق مرکزی طلا";
			case AccountingSubject.MANUALLY_UPDATE_BAAZAR:
				return "تحویل فیزیکی طلا";
			case AccountingSubject.MANUALLY_UPDATE_RIAL:
				return "تحویل نقدی";
			case AccountingSubject.SELL_BAAZAR_AUTOMATICALLY:
				return "فروش خودکار طلا";
			case AccountingSubject.SELL_BAAZAR_CREDIT_TIMED_OUT:
				return "فروش خودکار طلای اعتباری";
			case AccountingSubject.SELL_BAAZAR_WAGE:
				return "کارمزد فروش طلا";
			case AccountingSubject.TRANSFER_BAAZAR:
				return "انتقال طلا";
			case AccountingSubject.TRANSFER_BAAZAR_WAGE:
				return "کارمزد انتقال طلا";
			default:
				return "-";
		}
	}

	const columns = [
		{
			title: "نوع تراکنش",
			dataIndex: "accountingSubject",
			key: "accountingSubject",
			filters: Object.keys(AccountingSubject).map(value => {
				return { text: accountingSubjectFormatted(value), value };
			}),
			render: (data: any) => {
				return <span>{accountingSubjectFormatted(data)}</span>;
			},
		},
		{
			title: "افزایش/کاهش",
			dataIndex: "accountingStatus",
			key: "accountingStatus",
			filters: [
				{ text: "بدهکار", value: "DEBTOR" },
				{ text: "بستانکار", value: "CREDITOR" },
			],
			render: (data: any) => {
				return accountingStatusFormatted(data);
			},
		},
		{
			title: "مقدار طلا",
			dataIndex: "baazarAmount",
			key: "baazarAmount",
			render: (data: any) => {
				return <span>{data ? `${data} گرم` : "-"}</span>;
			},
		},
		{
			title: "مبلغ",
			dataIndex: "rialAmount",
			key: "rialAmount",
			render: (data: any) => {
				return <span>{data ? `${addCommas(data)} ریال` : "-"}</span>;
			},
		},
		{
			title: "موجودی ریالی",
			dataIndex: "currentRialAmount",
			key: "currentRialAmount",
			render: (data: any) => {
				return <span>{data ? `${addCommas(data)} ریال` : "-"}</span>;
			},
		},
		{
			title: "موجودی طلا",
			dataIndex: "currentBaazarAmount",
			key: "currentBaazarAmount",
			render: (data: any) => {
				return <span>{data ? `${data} گرم` : "-"}</span>;
			},
		},
		{
			title: "تاریخ",
			dataIndex: "createdDate",
			key: "createdDate",
			render: (data: any) => {
				return <span dir={"ltr"}>{jMoment(data * 1000).format("jYYYY/jMM/jDD HH:mm")}</span>;
			},
		},
	];

	return (
		<>
			<TitleBox title="اسناد مالی" />
			<div className={styles["financial"]}>
				<Table
					bordered
					loading={loading}
					pagination={pagination}
					className={styles["table"]}
					onChange={handleTableChange}
					scroll={{ x: true, y: false }}
					dataSource={
						!isEmpty(financials)
							? financials.map((data: FinancialType, key: number) => ({
									key,
									id: data.id,
									rialAmount: data.rialAmount,
									baazarAmount: data.baazarAmount,
									description: data.description,
									createdDate: data.createdDate,
									accountingStatus: data.accountingStatus,
									accountingSubject: data.accountingSubject,
									currentRialAmount: data.currentRialAmount,
									currentBaazarAmount: data.currentBaazarAmount,
							  }))
							: []
					}
					columns={columns}
				/>
			</div>
		</>
	);
};

export default Financials;
